import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type DependencyList,
  type ReactNode,
} from 'react';
import { Link } from 'react-router';

import { Icon } from '../ui/Icon';
import styles from './app.module.css';

/**
 * What a screen puts in the app bar. The design gives every screen its own bar rather than one
 * global title - Captura and Notas carry a title plus a trailing control, Alertas extends the
 * bar downward to hold its filter toggle, and Revisar nota adds a back control, a subtitle, and
 * a status pill (`docs/design/visual-system.md` §4).
 */
export interface AppBarContent {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  /** Leading back control. Omitted on the four navigation destinations, which are roots. */
  readonly back?: { readonly to: string; readonly label: string };
  /** Trailing slot. Empty when a screen declares nothing. */
  readonly trailing?: ReactNode;
  /** Full-width row below the title, inside the bar's fill - Alertas' Abiertas/Resueltas toggle. */
  readonly below?: ReactNode;
}

const AppBarContext = createContext<((content: AppBarContent | null) => void) | null>(null);

/**
 * Publishes this screen's app bar. Takes a factory and an explicit dependency list, like
 * `useMemo`, because the content carries elements: comparing those by identity every render
 * would republish forever. The factory itself is held in a ref so a caller does not have to
 * memoize it - only the values it reads belong in `deps`.
 */
export function useAppBar(factory: () => AppBarContent, deps: DependencyList): void {
  const setContent = useContext(AppBarContext);
  const factoryRef = useRef(factory);

  // Declared first, so it has already refreshed the ref by the time the publishing effect below
  // runs on any re-render.
  useEffect(() => {
    factoryRef.current = factory;
  });

  useEffect(() => {
    setContent?.(factoryRef.current());
    return () => {
      setContent?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the caller's own deps.
  }, [setContent, ...deps]);
}

export interface AppBarProviderProps {
  readonly children: (bar: AppBarContent | null) => ReactNode;
}

/** Holds the current screen's bar content and hands it to `AppLayout` to render. */
export function AppBarProvider({ children }: AppBarProviderProps) {
  const [content, setContent] = useState<AppBarContent | null>(null);

  return <AppBarContext.Provider value={setContent}>{children(content)}</AppBarContext.Provider>;
}

/** Two letters from the owner's address - the app has no display name to draw from. */
export function ownerInitials(email: string): string {
  const local = email.split('@')[0] ?? '';
  const letters = local.replace(/[^\p{L}]/gu, '');
  return (letters.slice(0, 2) || '?').toUpperCase();
}

export interface AppBarProps {
  readonly content: AppBarContent | null;
}

/**
 * The bar itself: `--nv-color-brand-strong` fill, white ink, sticky under the update banner.
 * Renders a plain brand bar when no screen has published content (sign-in, error routes).
 */
export function AppBar({ content }: AppBarProps) {
  const trailing = content?.trailing ?? null;

  return (
    <header className={styles.header}>
      <div className={styles.headerMain}>
        {content?.back === undefined ? null : (
          <Link className={styles.headerBack} to={content.back.to} aria-label={content.back.label}>
            <Icon name="chevron-left" size={20} />
          </Link>
        )}
        <div className={styles.headerTitles}>
          <h1 className={styles.headerTitle}>{content?.title ?? 'NoteVision'}</h1>
          {content?.subtitle === undefined ? null : (
            <p className={styles.headerSubtitle}>{content.subtitle}</p>
          )}
        </div>
        {trailing}
      </div>
      {content?.below ?? null}
    </header>
  );
}
